import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import AddPaymentInfoForm, { createEmptyPaymentInfo, PaymentInfoFormData } from '@/pages/Supporteds/AddPaymentInfoPopup/AddPaymentInfoForm';
import Button from '@/components/ui/Button';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import { PaymentMethodsDtoIn } from '@/models/in/PaymentMethodsDtoIn';
import { BankAccountDetailsDtoOut } from '@/models/out/BankAccountDetailsDtoOut';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import addIcon from '@/assets/icons/add.svg';

interface AddBankAccountInlineProps {
  fundId: number;
  supportedId: number;
  /** Whether the supported already has an account to pick. Without one the form opens by itself. */
  hasAccounts: boolean;
  /** The account the server created. The caller picks it for the request and refreshes its list. */
  onAdded: (account: BankAccountDtoIn) => void | Promise<void>;
}

/**
 * SPR-10282: a supported with no bank account was a dead end on the payment step - the «אופן
 * קבלת התשלום» list was empty and the coordinator had to leave the request, add the account on the
 * supporteds screen and start over. The same fields the «פרטי תשלום» window uses sit here under the
 * list instead, and the account saved is picked for the request straight away. Inline rather than a
 * second dialog: two stacked dialogs fight over focus (see NewSupportRequest's add-supported flow).
 */
const AddBankAccountInline = ({ fundId, supportedId, hasAccounts, onAdded }: AddBankAccountInlineProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(!hasAccounts);
  const [account, setAccount] = useState<PaymentInfoFormData>(createEmptyPaymentInfo);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: addAccount } = usePostAuthApi<PaymentMethodsDtoIn, BankAccountDetailsDtoOut[]>(
    fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/payment-infos` : ''
  );

  const isComplete = !!(account.accountOwnerName.trim() && account.bankNumber && account.branchNumber && account.accountNumber);
  const isPristine = !account.accountOwnerName && !account.bankNumber && !account.branchNumber && !account.accountNumber;

  // Opened by itself while the accounts were still loading: fold once they arrive, unless typing began.
  useEffect(() => {
    if (hasAccounts && isPristine) setIsOpen(false);
    // Only a change in hasAccounts should fold the form - not every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccounts]);

  const close = () => {
    setIsOpen(false);
    setAccount(createEmptyPaymentInfo());
    setError(null);
  };

  const handleSave = async () => {
    if (!isComplete || isSaving) return;
    setError(null);
    setIsSaving(true);
    try {
      const result = await addAccount([
        {
          type: PaymentMethodType.BankAccount,
          bankNumber: account.bankNumber,
          branchNumber: account.branchNumber,
          accountNumber: account.accountNumber,
          accountOwnerName: account.accountOwnerName.trim(),
          documents: [],
        },
      ]);
      // The server answers with the accounts it created - here exactly one.
      const created = result?.accounts?.[0];
      if (!created) {
        setError(t('errorAddingPaymentInfo'));
        return;
      }
      await onAdded(created);
      close();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError(t('duplicatePaymentInfoError'));
      } else {
        setError(t('errorAddingPaymentInfo'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="mt-2 flex flex-col gap-1">
        {!hasAccounts && (
          <p className="body-sm-regular text-(--font-body-2)" role="status">
            {t('supportRequestBankAccount.noAccountHint')}
          </p>
        )}
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 body-md-regular text-(--font-link) hover:underline cursor-pointer self-start"
        >
          <img src={addIcon} alt="" className="w-3 h-3" />
          <span>{t('supportRequestBankAccount.add')}</span>
        </button>
      </div>
    );
  }

  return (
    <section
      className="mt-4 flex flex-col gap-6 rounded-2xl border border-(--border-lowest) bg-(--surface-grey-lowest) p-6 max-md:p-4"
      aria-label={t('supportRequestBankAccount.title')}
    >
      <div className="flex flex-col gap-1">
        <h3 className="body-lr-semibold text-(--font-sub-title-2)">{t('supportRequestBankAccount.title')}</h3>
        {!hasAccounts && <p className="body-sm-regular text-(--font-body-2)">{t('supportRequestBankAccount.noAccountHint')}</p>}
      </div>

      <AddPaymentInfoForm single paymentInfos={[account]} onPaymentInfosChange={(list) => setAccount(list[0] ?? createEmptyPaymentInfo())} />

      {error && (
        <p className="body-md-regular text-(--font-error)" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-3 flex-wrap">
        <Button variant="primary" onClick={handleSave} disabled={!isComplete || isSaving}>
          {isSaving ? t('saving') : t('supportRequestBankAccount.save')}
        </Button>
        <Button variant="white" onClick={close} disabled={isSaving}>
          {t('cancel')}
        </Button>
      </div>
    </section>
  );
};

export default AddBankAccountInline;
